import type {
  Follow,
  StopLoader,
  PaginatedLoader,
  PaginatedGetItem,
  PaginatedLoadingContextValue,
  LoadedRange,
} from "@tandemdiabetes/sugarmate-api-react-client"
import { createPaginatedLoadingContextAndProvider } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import type {
  FC,
  ReactNode,
} from "react"
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react"
import type { RxCollection } from "rxdb"
import { useRxCollection } from "rxdb-hooks"
import type { LoadingLifecycleContextValue } from "./createLoadingLifecycleContextAndProvider"
import {
  createLoadingLifecycleContextAndProvider,
  defaultLoadingLifecycleContextValue,
} from "./createLoadingLifecycleContextAndProvider"
import { createLocalStorageGetterSetter } from "./createLocalStorageGetterSetter"
import type { UseReloadingContext } from "./createReloadingContextProvider"
import { createReloadingContextProvider } from "./createReloadingContextProvider"
import { useDbContext } from "src/contexts/DbContext"
import { useFollowedUsers } from "src/contexts/FollowedUsersContext"
import { usePageLifecycleContext } from "src/contexts/PageLifecycleContext"
import {
  useRefValue,
  usePromise,
} from "src/hooks/utils"
import type {
  CollectionKey,
  LocalStorageKey,
} from "src/types"
import { PageLifecycle } from "src/types"

interface Props {
  children?: ReactNode;
}

type LoadedRangesById = Record<Follow["following_account_id"], LoadedRange[]>

function serializeLoadedRanges(value: LoadedRangesById): string {
  return JSON.stringify(value)
}

function deserializeLoadedRanges(
  storedValue: string,
  defaultValue: LoadedRangesById,
): LoadedRangesById {
  try {
    const loadedRangesById = JSON.parse(storedValue) as LoadedRangesById
    return loadedRangesById
  } catch {
    return defaultValue
  }
}

function defaultUseLoading() {
  return defaultLoadingLifecycleContextValue
}

type Params<T> = {
  collectionKey: CollectionKey;
  localStorageKey: LocalStorageKey;
  useLoader: () => [
    PaginatedLoadingContextValue,
    PaginatedLoader<T>,
    StopLoader,
  ];
  useDependencyLoadingLifecycle?: () => LoadingLifecycleContextValue;
  useGetItems: () => undefined | PaginatedGetItem<T>;
}

/*
 * this function returns a provider for the loading context of a specific
 * model and a cook for consuming that context
 *
 * the provider handles executing the loader returned by "useLoader" at
 * the appropriate time
 */
export function createPaginatedLoadingContextProvider<T>(
  params: Params<T>,
): [
  FC<Props>,
  () => PaginatedLoadingContextValue,
  () => LoadingLifecycleContextValue,
  UseReloadingContext,
] {
  const {
    collectionKey,
    localStorageKey,
    useLoader,
    useDependencyLoadingLifecycle = defaultUseLoading,
    useGetItems,
  } = params

  const [
    useLoadingContext,
    LoadingContextProvider,
  ] = createPaginatedLoadingContextAndProvider()

  const [
    useLoadingLifecycleContext,
    LoadingLifecycleContextProvider,
  ] = createLoadingLifecycleContextAndProvider(useLoadingContext)

  const [
    ReloadingContextProvider,
    useReloadingContext,
  ] = createReloadingContextProvider()

  const [
    getAggregatedLoadedRanges,
    setAggregatedLoadedRanges,
  ] = createLocalStorageGetterSetter({
    key: localStorageKey,
    serializeValue: serializeLoadedRanges,
    deserializeValue: deserializeLoadedRanges,
  })

  const LoadingProvider = (props: Props) => {
    const rxCollection = useRxCollection(collectionKey) as RxCollection<T>
    const { isLoadingLeader } = useDbContext()
    const {
      currentUserId,
      currentFollow,
    } = useFollowedUsers()
    const currentFollowId = currentFollow?.id
    const getItems = useGetItems()
    const pageLifecycle = usePageLifecycleContext()
    const { loadedOrErrored: dependencyLoadedOrErrored } = useDependencyLoadingLifecycle()

    // the loadedRangesPromise will resolve when loaded from local storage
    const [
      loadedRangesPromise,
      resolveLoadedRanges,
    ] = usePromise<void>()

    // the valueRef will hold the current value of the loaded ranges
    const valueRef = useRef<LoadedRangesById>({})

    // get the loaded ranges from local storage
    useEffect(
      () => {
        void (async () => {
          const value = await getAggregatedLoadedRanges({})
          valueRef.current = value
          resolveLoadedRanges(undefined)
        })()
      },
      [
        valueRef,
        resolveLoadedRanges,
      ],
    )

    const getLoadedRangesForAccountId = useCallback(
      async (
        accountId: Follow["following_account_id"],
        defaultValue: LoadedRange[],
      ) => {
        await loadedRangesPromise
        const value = valueRef.current
        return value[accountId] ?? defaultValue
      },
      [
        loadedRangesPromise,
        valueRef,
      ],
    )

    const setLoadedRangesForAccountId = useCallback(
      (
        accountId: Follow["following_account_id"],
        loadedRanges: LoadedRange[],
      ) => {
        const newValue = {
          ...valueRef.current,
          [accountId]: loadedRanges,
        }

        valueRef.current = newValue
        return setAggregatedLoadedRanges(newValue)
      },
      [valueRef],
    )

    const upsertItems = useMemo(
      () => !rxCollection
        ? undefined
        : async (items: T[]) => {
          try {
            const response = await rxCollection.bulkUpsert(items)
            return response
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.log(
                `failed to insert ${items.length} items into ${collectionKey}`,
              )
            }
            throw e
          }
        },
      [rxCollection],
    )

    const [
      loadingState,
      loader,
      stopLoader,
    ] = useLoader()

    const isVisible = pageLifecycle === PageLifecycle.Active || pageLifecycle === PageLifecycle.Passive

    // request recent readings each time the app is foregrounded
    const requestRecentReadings = useMemo(
      () => !getItems || !isVisible
        ? undefined
        : async () => {
          try {
            const [items] = await getItems({
              params: {
                after: dayjs().subtract(
                  15,
                  "minutes",
                )
                  .toISOString(),
                all_follows: true,
              },
            })

            if (!items.length) {
              return
            }

            await rxCollection?.bulkUpsert(items)
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.log(e)
              console.log("couldn't fetch recent items")
            }
          }
        },
      [
        rxCollection,
        isVisible,
        getItems,
      ],
    )

    const requestRecentReadingsRef = useRefValue(requestRecentReadings)

    useEffect(
      () => {
        if (requestRecentReadings) {
          void requestRecentReadings()
        }
      },
      [requestRecentReadings],
    )

    const callLoader = useCallback(
      async () => {
        if (
          currentUserId &&
          currentFollowId &&
          loader &&
          upsertItems &&
          isVisible &&
          isLoadingLeader &&
          dependencyLoadedOrErrored
        ) {
          const after = dayjs()
            .subtract(
              90,
              "days",
            )
            .startOf("day")
            .toISOString()

          try {
            const getLoadedRanges = (defaultValue: LoadedRange[]) => (
              getLoadedRangesForAccountId(
                currentUserId,
                defaultValue,
              )
            )

            const setLoadedRanges = (loadedRanges: LoadedRange[]) => (
              setLoadedRangesForAccountId(
                currentUserId,
                loadedRanges,
              )
            )

            await loader(
              upsertItems,
              getLoadedRanges,
              setLoadedRanges,
              {
                followId: currentFollowId,
                after,
              },
            )
          } catch (e) {
            if (process.env.NODE_ENV === "development") {
              console.log(`loading ${collectionKey} failed for ${currentUserId}`)
              console.log(e)
            }
          }
        }
      },
      [
        loader,
        upsertItems,
        isVisible,
        currentUserId,
        currentFollowId,
        isLoadingLeader,
        getLoadedRangesForAccountId,
        setLoadedRangesForAccountId,
        dependencyLoadedOrErrored,
      ],
    )

    const callLoaderRef = useRefValue(callLoader)

    useEffect(
      () => {
        // for the current user call the loader immediately
        void callLoader()

        return () => {
          stopLoader()
        }
      },
      [
        callLoader,
        stopLoader,
      ],
    )

    const { reloadRequestedAt } = useReloadingContext()

    // execute the loader if a reload is requested
    useEffect(
      () => {
        if (reloadRequestedAt) {
          void requestRecentReadingsRef.current?.()
          void callLoaderRef.current()
        }
      },
      [
        reloadRequestedAt,
        callLoaderRef,
        requestRecentReadingsRef,
      ],
    )

    return (
      <LoadingContextProvider {...loadingState}>
        <LoadingLifecycleContextProvider
          loadingDependencies={[
            loader,
            upsertItems,
            isLoadingLeader,
            isVisible,
            currentFollowId,
            currentUserId,
            getLoadedRangesForAccountId,
            setLoadedRangesForAccountId,
            dependencyLoadedOrErrored,
          ]}
          loadingRequirements={[
            isVisible,
            isLoadingLeader,
          ]}
        >
          {props.children}
        </LoadingLifecycleContextProvider>
      </LoadingContextProvider>
    )
  }

  const Container = (props: Props) => (
    <ReloadingContextProvider>
      <LoadingProvider>
        {props.children}
      </LoadingProvider>
    </ReloadingContextProvider>
  )

  return [
    Container,
    useLoadingContext,
    useLoadingLifecycleContext,
    useReloadingContext,
  ]
}
